import { useEffect, useRef, type ReactNode } from 'react';
import { shortcutsRegistry } from './registry';
import type { Shortcut } from './types';

const CHORD_TIMEOUT_MS = 1500;
const DEBOUNCE_MS = 100;

/**
 * Mounts the global keydown listener that matches against the shortcuts
 * registry. Chord support: pressing `g` then `d` within {@link CHORD_TIMEOUT_MS}
 * fires a `'g d'` shortcut; the buffer resets on timeout or non-matching key.
 *
 * Focus suppression: shortcuts are ignored when focus is inside an INPUT,
 * TEXTAREA, [contenteditable], or SELECT element — typing should never fire
 * a global. Single-character keys are the only ones suppressed; the
 * `?` shortcut still fires from a contenteditable because it requires Shift,
 * which the user explicitly pressed.
 *
 * Each shortcut is debounced by id ({@link DEBOUNCE_MS} ms) to swallow OS
 * key-repeat.
 */
export function ShortcutsProvider({ children }: { children: ReactNode }): JSX.Element {
  const chordBufferRef = useRef<string>('');
  const chordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFiredAtRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function clearChord(): void {
      if (chordTimerRef.current) {
        clearTimeout(chordTimerRef.current);
        chordTimerRef.current = null;
      }
      chordBufferRef.current = '';
    }

    function tryFire(shortcut: Shortcut): void {
      const now = Date.now();
      const last = lastFiredAtRef.current.get(shortcut.id) ?? 0;
      if (now - last < DEBOUNCE_MS) return;
      lastFiredAtRef.current.set(shortcut.id, now);
      void shortcut.run();
    }

    function handler(event: KeyboardEvent): void {
      // Ignore standalone modifier keys.
      if (
        event.key === 'Shift' ||
        event.key === 'Control' ||
        event.key === 'Meta' ||
        event.key === 'Alt'
      ) {
        return;
      }

      const active = shortcutsRegistry.listActive();
      if (active.length === 0) return;

      // Build the canonical key token for this event.
      const isSingleChar = event.key.length === 1;
      const isPrintableNoMod =
        isSingleChar && !event.metaKey && !event.ctrlKey && !event.altKey;

      // Suppress only single-character no-modifier keys when typing.
      if (isPrintableNoMod && isTypingTarget(event.target)) {
        clearChord();
        return;
      }

      const token = canonicalToken(event);
      if (!token) return;

      // Try chord first: append to buffer + see if any shortcut matches.
      const candidate = chordBufferRef.current
        ? `${chordBufferRef.current} ${token}`
        : token;

      const match = active.find((s) => s.keys === candidate);
      if (match) {
        event.preventDefault();
        clearChord();
        tryFire(match);
        return;
      }

      // No full match — could this be a prefix of a chord?
      const isPrefix = active.some((s) => s.keys.startsWith(`${candidate} `));
      if (isPrefix) {
        chordBufferRef.current = candidate;
        if (chordTimerRef.current) clearTimeout(chordTimerRef.current);
        chordTimerRef.current = setTimeout(() => {
          chordBufferRef.current = '';
          chordTimerRef.current = null;
        }, CHORD_TIMEOUT_MS);
        return;
      }

      // Not a prefix and not a match — reset.
      clearChord();
    }

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (chordTimerRef.current) clearTimeout(chordTimerRef.current);
    };
  }, []);

  return <>{children}</>;
}

/**
 * Build the canonical token for a keydown event:
 *   - `'g'`, `'d'`, `'?'` for unmodified single keys
 *   - `'Cmd+K'` / `'Ctrl+K'` for modifier combos
 *   - `'Escape'` / `'Enter'` for named keys
 *
 * Returns null if the event doesn't represent a usable shortcut input.
 */
function canonicalToken(event: KeyboardEvent): string | null {
  const key = event.key;
  if (!key) return null;

  if (event.metaKey || event.ctrlKey) {
    const mod = event.metaKey ? 'Cmd' : 'Ctrl';
    if (key.length === 1) return `${mod}+${key.toUpperCase()}`;
    return `${mod}+${key}`;
  }

  return key;
}
